// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { Tree, TreeNode } from "react-organizational-chart";

import { IAction } from "actions";
import { isSet } from "utils";
import { getEnergySourceName } from "guiutils";

import TariffCircuitListModel from "model/tariffcircuitlist";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  IChartTariffNode,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  ITrendTimestamps,
} from "./ChartBase";
import { IState } from "states/states";

interface IHierarchyChartExtProps extends IChartBaseExtProps {}

interface IHierarchyChartProps extends IChartBaseProps {}

interface IHierarchyChartDispatch extends IChartBaseDispatch {}

interface IHierarchyChartState extends IChartBaseState {}

interface IHierarchyTreeNode {
  tariffCircuit: IChartTariffNode;
  childCircuits: IHierarchyTreeNode[];
}

const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const props = {
    ...baseMapStateToProps(state, ownProps),
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IHierarchyChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
  };
  return dispatchProps;
};

class HierarchyChartComponent extends ChartBaseComponent<
  IHierarchyChartProps & IHierarchyChartExtProps & IHierarchyChartDispatch,
  IHierarchyChartState
> {
  state: IHierarchyChartState = {
    showZeroValues: true,
  };

  // gets the whole subtree of all item indexes with given parent
  private getTcTree(
    allTcIndexes: number[],
    selectedParentId: number | null | undefined,
    trendTimestamps: ITrendTimestamps,
    ignoreColoring: boolean,
    recursionLevel = 0,
  ): IHierarchyTreeNode {
    // ensure suitable recursion level; overflow may happen only in illegal parameter set
    console.assert(recursionLevel < 20, "hierarchy recursion level overflow");
    // get index of current item
    const tcIndex = this.props.tariffCircuits.findIndex(
      (curItem, curIndex) => (curItem.id ?? curIndex) === selectedParentId,
    );
    console.assert(tcIndex >= 0, "Tariff circuit index not found", selectedParentId);
    // gets all item indexes with given parent
    const children =
      recursionLevel < 20
        ? allTcIndexes.filter((curIndex) =>
            isSet(selectedParentId)
              ? this.props.tariffCircuits[curIndex].parent === selectedParentId
              : !isSet(this.props.tariffCircuits[curIndex].parent),
          )
        : [];
    return {
      tariffCircuit: this.makeChartTariffNode(tcIndex, trendTimestamps, ignoreColoring),
      childCircuits: children.map((curIndex) =>
        this.getTcTree(
          allTcIndexes,
          this.props.tariffCircuits[curIndex].id ?? curIndex,
          trendTimestamps,
          ignoreColoring,
          recursionLevel + 1,
        ),
      ),
    };
  }

  private prepareChartData() {
    const trendTimestamps = this.getTimestampsForTrendData(
      this.props.trendData,
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod,
      false,
    );
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    // get list of all tariff circuits, grouped by energy source
    const energySources = this.props.selectedEnergySource
      ? [this.props.selectedEnergySource]
      : (tcModel.getEnergySources(false) as string[]);
    const chartData: IHierarchyTreeNode[][] = [];

    energySources.forEach((curEnergySource) => {
      // all tariff circuits for current energy source
      const allTcIndexes = tcModel.getTariffCircuits(
        curEnergySource,
        false,
        undefined,
        true,
        undefined,
        true,
      );
      // get root nodes
      const rootNodeIndexes = allTcIndexes.filter((curIndex) =>
        isSet(this.props.selectedHierarchyId)
          ? (this.props.tariffCircuits[curIndex].id ?? curIndex) === this.props.selectedHierarchyId
          : !isSet(this.props.tariffCircuits[curIndex].parent),
      );
      const rootNodeIds = rootNodeIndexes.map(
        (curIndex) => this.props.tariffCircuits[curIndex].id ?? curIndex,
      );

      // get hierarchical tree of all root nodes
      const rootNodeTrees = rootNodeIds.map((curRoot) =>
        this.getTcTree(allTcIndexes, curRoot, trendTimestamps, false),
      );
      chartData.push(rootNodeTrees);
    });
    return { chartData, energySources };
  }

  private renderTrees() {
    const { chartData, energySources } = this.prepareChartData();

    return (
      <>
        {chartData.map((anEnergyNodeInfo, eIndex) => (
          <React.Fragment key={`tariffNode_${eIndex}`}>
            {chartData.length > 1 && anEnergyNodeInfo.length > 0 && (
              <h2 className="sourceheading">{getEnergySourceName(energySources[eIndex])}</h2>
            )}
            {anEnergyNodeInfo.map((aNodeInfo, index) => {
              return (
                <div key={`TreeRootDiv_${eIndex}_${index}`} className="hierarchyTree mb-3">
                  <Tree
                    lineBorderRadius="10px"
                    lineColor="lightgray"
                    lineHeight={aNodeInfo.childCircuits.length > 0 ? "30px" : "0px"}
                    lineStyle="solid"
                    label={this.renderStyledDiv(
                      aNodeInfo.tariffCircuit.id,
                      aNodeInfo.tariffCircuit.name,
                      aNodeInfo.tariffCircuit.valueText,
                      `${aNodeInfo.tariffCircuit.color}80`,
                      this.props.tariffCircuits.some(
                        (tariff) =>
                          tariff.parent === aNodeInfo.tariffCircuit.id &&
                          aNodeInfo.tariffCircuit.id !== this.props.selectedHierarchyId,
                      ),
                    )}
                  >
                    {aNodeInfo.childCircuits.length > 0 ? (
                      aNodeInfo.childCircuits.map((aNode) => this.renderTreeNode(aNode, index))
                    ) : (
                      <></>
                    )}
                  </Tree>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  }

  private renderTreeNode(aTariff: IHierarchyTreeNode, index: number) {
    return (
      <TreeNode
        key={`TreeNode_${aTariff.tariffCircuit.id}`}
        label={this.renderStyledDiv(
          aTariff.tariffCircuit.id,
          aTariff.tariffCircuit.name,
          aTariff.tariffCircuit.valueText,
          `${aTariff.tariffCircuit.color}80`,
          this.props.tariffCircuits.some((tariff) => tariff.parent === aTariff.tariffCircuit.id),
        )}
      >
        {aTariff.childCircuits.map((aNode) => this.renderTreeNode(aNode, index))}
      </TreeNode>
    );
  }

  private renderStyledDiv(
    id: number,
    label: string,
    value: string,
    color: string,
    clickable: boolean,
  ) {
    return (
      <div
        style={{
          background: color,
          border: "1px solid " + color,
        }}
        className={`hierachyTreeNode ${clickable ? "link" : ""}`}
        onClick={() => {
          if (clickable) {
            this.props.onHierarchyChanged(this.props.selectedDashboardIndex, id);
          }
        }}
      >
        <div>
          <div>{label}</div>
          <div className={"treeNodeValue"}>{value}</div>
        </div>
      </div>
    );
  }

  // scroll each TreeNode on first drawing into the middle of the view
  public componentDidMount(): void {
    const elements = document.getElementsByClassName("hierarchyTree");
    Array.from(elements).forEach((element) => {
      element.scrollLeft = element.scrollWidth / 2 - element.clientWidth / 2;
    });
  }

  public render() {
    return this.renderTrees();
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(HierarchyChartComponent));
